import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const STATUS_OPTIONS = ['출석', '결석', '지각', '조퇴', '공결'];
const MAX_PERIODS = 8;
const today = () => new Date().toISOString().slice(0, 10);

function defaultRow(active = {}) {
  const periodValues = {};
  for (let p = 1; p <= MAX_PERIODS; p += 1) {
    periodValues[p] = active[p] ? 1 : '';
  }
  return { attstatus: '출석', reason: '', periodValues };
}

function allPeriodsInactive() {
  const next = {};
  for (let p = 1; p <= MAX_PERIODS; p += 1) next[p] = false;
  return next;
}

// 세부교육내용 줄의 들여쓰기 단계를 판단한다: "2.10 ..." → 1단계, "가) ..." → 2단계, "- ..." → 3단계
function sectionLevel(text) {
  if (!text) return 1;
  if (/^\d+(\.\d+)*\s/.test(text)) return 1;
  if (/^[가-하]\)/.test(text)) return 2;
  if (text.startsWith('-')) return 3;
  return 1;
}

export default function Attendance() {
  const { user } = useAuth();
  const isAdminStaff = user?.role === 'admin' || user?.role === 'staff';
  const [satdate, setSatdate] = useState(today());
  const [grade, setGrade] = useState(1);
  const [classtype, setClasstype] = useState('M');
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [plans, setPlans] = useState([]);
  const [crsuid, setCrsuid] = useState('');
  const [daytime, setDaytime] = useState(MAX_PERIODS);
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [periodsActive, setPeriodsActive] = useState(allPeriodsInactive());
  const [rows, setRows] = useState({}); // studid -> { attstatus, reason, periodValues: {1..4} }
  // 이미 출결저장되어 있는 교시 번호(체크 해제해도 값이 남아있어야 하는 교시)와,
  // 그 교시들이 저장 당시 어떤 교육내용(amtcode)/학과·실기(tptype)였는지 -
  // 체크 해제된 교시는 저장 시 이 값을 그대로 다시 보낸다
  const [savedPeriods, setSavedPeriods] = useState(new Set());
  const [savedPeriodAmtcode, setSavedPeriodAmtcode] = useState({});
  const [savedPeriodTptype, setSavedPeriodTptype] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const year = useMemo(() => Number(satdate.slice(0, 4)), [satdate]);

  useEffect(() => {
    api.get('/lesson-plans', { params: { year } }).then((res) => {
      // '잠정' 교수계획서는 스마트폰에서만 출결처리 한다 (PC에서는 정규/보충/특별 등으로 수정한 뒤 처리)
      const rows = res.data.filter((p) => p.typecls !== '잠정');
      setPlans(rows);
      setCrsuid((prev) => (prev && rows.some((p) => p.crsuid === prev) ? prev : rows[0]?.crsuid ?? ''));
    });
  }, [year]);

  const selectedPlan = useMemo(() => plans.find((p) => p.crsuid === crsuid), [plans, crsuid]);

  // 한 차시가 여러 PART(챕터)를 아우를 수 있어, 세부교육내용을 PART(crsmatno)별로 따로 묶어서 보여준다
  const chapterGroups = useMemo(() => {
    if (!selectedPlan) return [];
    const order = [];
    const byKey = new Map();
    selectedPlan.sections.forEach((s) => {
      const key = s.crsmatno || s.chapter || selectedPlan.chapter;
      if (!byKey.has(key)) {
        byKey.set(key, {
          chapter: s.chapter || selectedPlan.chapter,
          crsmatno: s.crsmatno || key,
          amtcode: s.amtcode || null,
          sections: [],
          thrytime: 0,
          practime: 0,
        });
        order.push(key);
      }
      const g = byKey.get(key);
      g.sections.push(s);
      g.thrytime += s.thrytime || 0;
      g.practime += s.practime || 0;
    });
    return order.map((key) => byKey.get(key));
  }, [selectedPlan]);

  // 한 차시가 여러 지정과목 챕터를 아우를 때, 오늘 이 출결저장이 어느 챕터의 학과/실기
  // 수업인지 선택한 값 (crsmatno 기준으로 관리하고, 저장 시점에 해당 챕터의 amtcode로 변환해 보낸다)
  const [selectedChapterKey, setSelectedChapterKey] = useState('');
  const [selectedTptype, setSelectedTptype] = useState('T');

  useEffect(() => {
    const first = chapterGroups[0];
    setSelectedChapterKey(first?.crsmatno ?? '');
    setSelectedTptype(first && !first.thrytime && first.practime ? 'P' : 'T');
  }, [chapterGroups]);

  // 세부교육내용 팝업에 표시할 챕터(null이면 닫힘)
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);

  // 교수계획서를 선택하면 그 차시에 등록된 일일 수업시간을 기본값으로 채워준다 (직접 수정 가능)
  useEffect(() => {
    if (!selectedPlan) return;
    const fallback = (selectedPlan.thrytime || 0) + (selectedPlan.practime || 0);
    const next = selectedPlan.daytime || fallback || MAX_PERIODS;
    setDaytime(Math.min(MAX_PERIODS, Math.max(1, next)));
  }, [selectedPlan]);

  // 일일 수업시간 만큼만 교시 칸을 보여줌 (1이면 1교시만 ... 4면 1~4교시 모두)
  const periodCount = useMemo(
    () => Math.min(MAX_PERIODS, Math.max(1, Number(daytime) || 1)),
    [daytime]
  );

  const PERIODS = useMemo(() => Array.from({ length: periodCount }, (_, i) => i + 1), [periodCount]);

  useEffect(() => {
    if (!crsuid) {
      setStats(null);
      return;
    }
    api.get('/teacher/lesson-plan-stats', { params: { crsuid } }).then((res) => setStats(res.data));
  }, [crsuid]);

  // 학년/구분이 바뀌면 그에 맞는 반 목록만 새로 불러오고, 명단/출결은 반을 다시 고를 때까지 비워둠
  useEffect(() => {
    setClassNo('');
    setStudents([]);
    setRows({});
    setAlreadySubmitted(false);
    api.get('/classes', { params: { grade, classtype } }).then((res) => setClassOptions(res.data));
  }, [grade, classtype]);

  // 반을 실제로 선택했을 때만 해당 반 명단을 조회함 (화면이 열리자마자 자동 조회되지 않도록)
  useEffect(() => {
    if (classNo === '') {
      setStudents([]);
      return;
    }
    let cancelled = false;
    api.get('/students', { params: { grade, class_no: classNo, classtype } }).then((res) => {
      if (!cancelled) setStudents(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [grade, classNo, classtype]);

  const loadSession = useCallback(async () => {
    if (!crsuid || classNo === '') {
      setRows({});
      setAlreadySubmitted(false);
      setPeriodsActive(allPeriodsInactive());
      setSavedPeriods(new Set());
      setSavedPeriodAmtcode({});
      setSavedPeriodTptype({});
      return;
    }
    const res = await api.get('/teacher/attendance', {
      params: { satdate, crsuid, classtype, class_no: classNo },
    });
    if (res.data.length > 0) {
      const next = {};
      const loadedPeriods = new Set();
      const amtcodeByPeriod = {};
      const tptypeByPeriod = {};
      res.data.forEach((r) => {
        loadedPeriods.add(r.period);
        if (!next[r.studid]) next[r.studid] = defaultRow();
        next[r.studid].periodValues[r.period] = r.crdtime;
        if (r.amtcode && !amtcodeByPeriod[r.period]) amtcodeByPeriod[r.period] = r.amtcode;
        if (r.tptype && !tptypeByPeriod[r.period]) tptypeByPeriod[r.period] = r.tptype;
        // 학생당 상태는 하나만 관리하므로, 출석이 아닌(조퇴 등) 기록이 있으면 그것을 우선함
        if (next[r.studid].attstatus === '출석' || r.attstatus !== '출석') {
          next[r.studid].attstatus = r.attstatus;
          next[r.studid].reason = r.reason || '';
        }
      });
      setRows(next);
      // 체크박스는 "지금 입력 중"인 교시만 표시한다 - 이미 저장된 교시는 값만 보여주고
      // 체크 해제(비활성) 상태로 시작해, 다시 손댈 때만 직접 체크하도록 한다.
      setPeriodsActive(allPeriodsInactive());
      setSavedPeriods(loadedPeriods);
      setSavedPeriodAmtcode(amtcodeByPeriod);
      setSavedPeriodTptype(tptypeByPeriod);
      setAlreadySubmitted(true);
    } else {
      setAlreadySubmitted(false);
      setRows({});
      setPeriodsActive(allPeriodsInactive());
      setSavedPeriods(new Set());
      setSavedPeriodAmtcode({});
      setSavedPeriodTptype({});
    }
  }, [satdate, crsuid, classtype, classNo]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const rowFor = (studid) => rows[studid] || defaultRow(periodsActive);

  const updateRow = (studid, patch) => {
    setRows((prev) => ({ ...prev, [studid]: { ...rowFor(studid), ...patch } }));
  };

  const updatePeriodValue = (studid, period, value) => {
    const current = rowFor(studid);
    setRows((prev) => ({
      ...prev,
      [studid]: { ...current, periodValues: { ...current.periodValues, [period]: value } },
    }));
  };

  // 결석/지각/조퇴는 그 교시에 시수를 인정하지 않는다 (공결은 출석과 동일하게 정상 인정)
  const ABSENCE_LIKE_STATUSES = ['결석', '지각', '조퇴'];

  const togglePeriodActive = (period) => {
    setPeriodsActive((prev) => {
      const nextValue = !prev[period];
      // 이미 저장된 교시(savedPeriods)는 체크 여부와 무관하게 저장된 값을 항상 그대로 보여준다.
      // 저장되지 않은 교시만 체크 시 1(결석 등은 0)로 채우고, 체크 해제 시 값을 비워 혼동을 없앤다.
      if (!savedPeriods.has(period)) {
        setRows((prevRows) => {
          const updated = {};
          Object.entries(prevRows).forEach(([studid, row]) => {
            const fillValue = ABSENCE_LIKE_STATUSES.includes(row.attstatus) ? 0 : 1;
            updated[studid] = {
              ...row,
              periodValues: {
                ...row.periodValues,
                [period]: nextValue ? fillValue : '',
              },
            };
          });
          return updated;
        });
      }
      return { ...prev, [period]: nextValue };
    });
  };

  const creditFor = (studid) => {
    const r = rowFor(studid);
    return Array.from({ length: MAX_PERIODS }, (_, i) => i + 1)
      .filter((p) => periodsActive[p] || savedPeriods.has(p))
      .reduce((sum, p) => sum + (Number(r.periodValues[p]) || 0), 0);
  };

  // 체크된 교시의 저장된 출석자료를 삭제한다 (체크 안 된, 기존에 저장된 다른 교시는 그대로 보존)
  const handleReset = async (periodsToClear) => {
    setSaving(true);
    setMessage('');
    try {
      const periodsToKeep = Array.from({ length: MAX_PERIODS }, (_, i) => i + 1).filter(
        (p) => savedPeriods.has(p) && !periodsToClear.includes(p)
      );
      await api.post('/teacher/attendance', {
        satdate,
        grade: Number(grade),
        classtype,
        class_no: Number(classNo),
        crsuid,
        entries: students.map((s) => {
          const r = rowFor(s.studid);
          return {
            studid: s.studid,
            attstatus: r.attstatus,
            reason: r.reason || null,
            periods: periodsToKeep.map((p) => ({
              period: p,
              crdtime: Number(r.periodValues[p]) || 0,
              amtcode: savedPeriodAmtcode[p] || null,
              tptype: savedPeriodTptype[p] || null,
            })),
          };
        }),
      });
      setMessage('선택한 교시의 출석자료가 삭제되었습니다.');
      await loadSession();
    } catch (err) {
      setMessage(err.response?.data?.detail || '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 체크된 교시를 확인 후 삭제한다
  const handleResetClick = () => {
    const periodsToClear = PERIODS.filter((p) => periodsActive[p]);
    if (periodsToClear.length === 0) {
      setMessage('초기화할 교시를 먼저 체크하세요.');
      return;
    }
    const periodLabel = periodsToClear.map((p) => `${p}교시`).join(', ');
    if (window.confirm(`${periodLabel} 출석자료를 삭제합니다.\n\n정말 삭제하시겠습니까?`)) {
      handleReset(periodsToClear);
    }
  };

  // 이 교시에 출결처리 할 챕터의 amtcode (라디오로 고른 crsmatno를 amtcode로 환산)
  const selectedChapterAmtcode = () => {
    const key = selectedChapterKey || chapterGroups[0]?.crsmatno;
    return chapterGroups.find((g) => g.crsmatno === key)?.amtcode || null;
  };

  const selectedChapterLabel = () => {
    const key = selectedChapterKey || chapterGroups[0]?.crsmatno;
    return chapterGroups.find((g) => g.crsmatno === key)?.chapter || selectedPlan?.chapter || '';
  };

  const tptypeLabel = (t) => (t === 'P' ? '실기' : t === 'T' ? '학과' : '');

  // 해당 교시가 학과/실기 중 어느 것으로 처리되는지. 지금 체크되어 다시 편집 중인 교시는
  // (이미 저장된 것이었더라도) 위 카드에서 새로 고른 학과/실기를 보여주고, 체크 안 된
  // 저장된 교시만 저장 당시 값을 그대로 보여준다.
  const periodTptypeLabel = (p) => {
    if (periodsActive[p]) return tptypeLabel(selectedTptype);
    if (savedPeriods.has(p)) return tptypeLabel(savedPeriodTptype[p]);
    return '';
  };

  // 입력칸에 "학과 1" / "실기 1" 처럼 종류+시수를 한 줄로 보여준다
  const periodInputValue = (p, raw) => {
    const label = periodTptypeLabel(p);
    if (!label) return raw === '' ? '' : String(raw);
    return raw === '' ? label : `${label} ${raw}`;
  };

  // 사용자가 입력한 텍스트에서 숫자만 뽑아 실제 시수 값으로 저장한다 (라벨 부분은 항상 자동으로 다시 붙음)
  const handlePeriodInputChange = (studid, p, rawText) => {
    const digits = rawText.replace(/\D/g, '');
    updatePeriodValue(studid, p, digits === '' ? '' : digits);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      // 현재 체크된 교시 + 체크는 해제됐지만 이미 저장돼 있던 교시(값을 보존해 다시 그대로 보냄)
      const periodsToSave = Array.from({ length: MAX_PERIODS }, (_, i) => i + 1).filter(
        (p) => periodsActive[p] || savedPeriods.has(p)
      );
      await api.post('/teacher/attendance', {
        satdate,
        grade: Number(grade),
        classtype,
        class_no: Number(classNo),
        crsuid,
        entries: students.map((s) => {
          const r = rowFor(s.studid);
          return {
            studid: s.studid,
            attstatus: r.attstatus,
            reason: r.reason || null,
            periods: periodsToSave.map((p) => ({
              period: p,
              crdtime: Number(r.periodValues[p]) || 0,
              // 체크 해제된 채 보존만 되는 교시는 저장 당시의 교육내용(amtcode)/학과·실기(tptype)를 그대로 유지한다
              amtcode: periodsActive[p] ? selectedChapterAmtcode() : savedPeriodAmtcode[p] || null,
              tptype: periodsActive[p] ? selectedTptype : savedPeriodTptype[p] || null,
            })),
          };
        }),
      });
      setMessage(alreadySubmitted ? '출결 정보가 수정되었습니다.' : '출결이 저장되었습니다.');
      await loadSession();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 저장 전, 어떤 교육내용(챕터)으로 출결처리 되는지 알려주고 확인/취소를 받는다
  const handleSaveClick = () => {
    if (satdate > today()) {
      setMessage('미래 날짜는 출결처리를 할 수 없습니다.');
      return;
    }
    const activePeriods = PERIODS.filter((p) => periodsActive[p]);
    if (activePeriods.length === 0) {
      handleSave();
      return;
    }
    const periodLabel = activePeriods.map((p) => `${p}교시`).join(', ');
    const message = `${periodLabel}에 "${selectedChapterLabel()}(${tptypeLabel(selectedTptype)})" 수업으로 출결처리 하겠습니다.\n\n이대로 저장하시겠습니까?`;
    if (window.confirm(message)) {
      handleSave();
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>출결관리</h2>
      </div>

      <div className="card">
        <div className="form-row attendance-plan-row">
          <div className="form-field">
            <label>교수계획서(차시)</label>
            <select value={crsuid} onChange={(e) => setCrsuid(Number(e.target.value))}>
              <option value="">-- 선택하세요 --</option>
              {plans.map((p) => (
                <option key={p.crsuid} value={p.crsuid}>
                  [{p.typecls || '정규'}{isAdminStaff && p.teacher_name ? `-${p.teacher_name}` : ''}] {p.subject} - {p.chapter} - {p.unitno}차시
                </option>
              ))}
            </select>
          </div>
        </div>

        {selectedPlan && (
          <div className="table-scroll plan-summary">
            <table className="data-table">
              <thead>
                <tr>
                  <th>담당교사</th>
                  <th>차수</th>
                  <th>시수</th>
                  <th>과목</th>
                  <th>교육내용</th>
                  <th>학과</th>
                  <th>실기</th>
                  <th>수업구분</th>
                </tr>
              </thead>
              <tbody>
                {chapterGroups.map((g, gi) => (
                  <tr key={g.crsmatno}>
                    {gi === 0 && (
                      <>
                        <td rowSpan={chapterGroups.length}>{selectedPlan.teacher_name}</td>
                        <td rowSpan={chapterGroups.length}>{selectedPlan.unitno}</td>
                        <td rowSpan={chapterGroups.length}>
                          {(selectedPlan.thrytime || 0) + (selectedPlan.practime || 0)}
                        </td>
                        <td rowSpan={chapterGroups.length}>{selectedPlan.subject}</td>
                      </>
                    )}
                    <td>
                      <button
                        type="button"
                        className="chapter-detail-link"
                        onClick={() => setDetailPopupOpen(true)}
                      >
                        {g.chapter}
                      </button>
                    </td>
                    <td>{g.thrytime}</td>
                    <td>{g.practime}</td>
                    <td className="chapter-radio-cell">
                      <div className="tptype-radio-group">
                        <label className="tptype-radio">
                          <input
                            type="radio"
                            name="chapter-tptype-select"
                            checked={selectedChapterKey === g.crsmatno && selectedTptype === 'T'}
                            onChange={() => {
                              setSelectedChapterKey(g.crsmatno);
                              setSelectedTptype('T');
                            }}
                          />
                          학과
                        </label>
                        <label className="tptype-radio">
                          <input
                            type="radio"
                            name="chapter-tptype-select"
                            checked={selectedChapterKey === g.crsmatno && selectedTptype === 'P'}
                            onChange={() => {
                              setSelectedChapterKey(g.crsmatno);
                              setSelectedTptype('P');
                            }}
                          />
                          실기
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detailPopupOpen && selectedPlan && (
          <div className="modal-overlay" onClick={() => setDetailPopupOpen(false)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedPlan.unitno}차시 세부교육내용</h3>
                <button className="btn-ghost" type="button" onClick={() => setDetailPopupOpen(false)}>닫기</button>
              </div>
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>교육내용</th>
                      <th>세부교육내용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chapterGroups.map((g) => (
                      <tr key={g.crsmatno}>
                        <td>{g.chapter}</td>
                        <td>
                          {g.sections.map((s) => {
                            const level = sectionLevel(s.sections);
                            return (
                              <React.Fragment key={s.crssectno}>
                                <div className={`att-section-line att-section-level-${level}`}>{s.sections}</div>
                                {s.remark && (
                                  <div className={`att-section-line att-section-level-${level + 1}`}>{s.remark}</div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="card attendance-process-card">
        <div className="attendance-top-row">
          <div className="form-row attendance-filters">
            <div className="form-field form-field-narrow">
              <label>날짜</label>
              <input
                type="date"
                value={satdate}
                max={today()}
                onChange={(e) => setSatdate(e.target.value)}
              />
            </div>
            <div className="form-field form-field-narrow">
              <label>구분</label>
              <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
                <option value="R">정규반</option>
                <option value="M">면장반</option>
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>학년</label>
              <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
                <option value={1}>1학년</option>
                <option value={2}>2학년</option>
                <option value={3}>3학년</option>
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>반</label>
              <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
                <option value="">-- 선택 --</option>
                {classOptions.map((c) => (
                  <option key={c} value={c}>{c}반</option>
                ))}
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>일일 수업시간</label>
              <select value={daytime} onChange={(e) => setDaytime(Number(e.target.value))}>
                {Array.from({ length: MAX_PERIODS }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {stats && (
            <div className="stat-mini-grid">
              <div className="stat-mini-card">
                <div className="stat-mini-value">{stats.total_hours}</div>
                <div className="stat-mini-label">총시수</div>
              </div>
              <div className="stat-mini-card">
                <div className="stat-mini-value">{stats.chapters.map((c) => c.assigned_total).join(' / ')}</div>
                <div className="stat-mini-label">과목시수</div>
              </div>
              <div className="stat-mini-card">
                <div className="stat-mini-value">{stats.chapters.map((c) => c.cumulative_hours).join(' / ')}</div>
                <div className="stat-mini-label">누적시수</div>
              </div>
              <div className="stat-mini-card">
                <div className="stat-mini-value">{stats.chapters.map((c) => c.session_hours).join(' / ')}</div>
                <div className="stat-mini-label">수업누계</div>
              </div>
            </div>
          )}
        </div>

        {alreadySubmitted && (
          <div className="form-message">이미 입력된 출결입니다. 저장하면 이 내용으로 덮어씁니다.</div>
        )}

        <div className="table-scroll attendance-table-wrap">
          <table className="data-table attendance-table">
            <thead>
              <tr>
                <th>학번</th>
                <th>이름</th>
                {PERIODS.map((p) => (
                  <th key={p} className="period-th">
                    <label className="period-header">
                      <input
                        type="checkbox"
                        checked={periodsActive[p]}
                        onChange={() => togglePeriodActive(p)}
                      />
                      {p}교시
                    </label>
                  </th>
                ))}
                <th>출결상태</th>
                <th>사유</th>
                <th>인정시수</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s, i) => {
                const r = rowFor(s.studid);
                return (
                  <tr key={s.studid}>
                    <td>{s.studno}</td>
                    <td>{s.name}</td>
                    {PERIODS.map((p) => (
                      <td key={p}>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="period-value-input"
                          disabled={!periodsActive[p]}
                          value={periodInputValue(p, r.periodValues[p])}
                          onChange={(e) => handlePeriodInputChange(s.studid, p, e.target.value)}
                        />
                      </td>
                    ))}
                    <td>
                      <select
                        value={r.attstatus}
                        onChange={(e) => {
                          const nextStatus = e.target.value;
                          // 지금 체크되어 있는(편집 중인) 교시만 상태에 맞춰 시수를 다시 채운다:
                          // 결석/지각/조퇴면 0, 출석/공결로 되돌리면 다시 1(인정)로 복원.
                          // 체크 안 된(편집 중이 아닌) 저장된 교시 값은 건드리지 않는다.
                          const fillValue = ABSENCE_LIKE_STATUSES.includes(nextStatus) ? 0 : 1;
                          const updatedValues = { ...r.periodValues };
                          for (let p = 1; p <= MAX_PERIODS; p += 1) {
                            if (periodsActive[p]) updatedValues[p] = fillValue;
                          }
                          updateRow(s.studid, { attstatus: nextStatus, periodValues: updatedValues });
                        }}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="reason-input"
                        disabled={r.attstatus === '출석'}
                        value={r.reason || ''}
                        onChange={(e) => updateRow(s.studid, { reason: e.target.value })}
                        placeholder={r.attstatus === '출석' ? '' : '사유 입력'}
                      />
                    </td>
                    <td className="credit-total-cell">{creditFor(s.studid)}</td>
                  </tr>
                );
              })}
              {students.length === 0 && (
                <tr>
                  <td colSpan={PERIODS.length + 5} className="empty-row">
                    {classNo === '' ? '반을 선택하면 명단이 표시됩니다.' : '해당 학년/반 학생이 없습니다.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {message && <div className="form-message">{message}</div>}

        <div className="attendance-actions">
          <button className="btn-ghost" type="button" onClick={handleResetClick} disabled={saving}>출석자료 초기화</button>
          <button className="btn-primary btn-inline" type="button" onClick={handleSaveClick} disabled={saving || students.length === 0}>
            {saving ? '저장 중...' : PERIODS.some((p) => periodsActive[p] && savedPeriods.has(p)) ? '수정 저장' : '출결 저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
